import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
    Breadcrumbs,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { Link as RouterLink, Redirect } from "react-router-dom";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import {Config} from "../../../config/config";
import { getGlobalVar, Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";

interface MapSettings {
    name: string | null;
    description: string | null;
    money: number;
    elixir: number;
    power: number;
}

interface MapEditorState {
    mapId: string;
    redirect: boolean;
    savingData: boolean;
    isLoaded: boolean;
    mapSettings: MapSettings;
    buildingsConfig: any[];
    items: Item[];
    rewardPopupVisible: boolean;
    victoryRewardPopupVisible: boolean;
    rewards: Reward[];
    missionType: string;
}

interface Reward {
    itemId: string;
    amount: number;
    level?: number;
}

interface Item {
    uniqueId: string;
    itemId: string;
    itemImage: HTMLImageElement;
    itemSize: number;
    x: number;
    y: number;
    allPositions: any[];
    level: number;
}

class MapEditorWrapped extends React.Component<{ activeUser: any; match: any; classes: any }, MapEditorState> {

    constructor(props) {
        super(props);

        this.state = {
            redirect: false,
            savingData: false,
            mapId: props.match.params.mapId,
            missionType: "attack",
            isLoaded: false,
            mapSettings: {
                name: null,
                description: null,
                money: 0,
                elixir: 0,
                power: 0,
            },
            items: [],
            buildingsConfig: [],
            rewardPopupVisible: false,
            victoryRewardPopupVisible: false,
            rewards: [],
        };
    }

    async componentDidMount() {
        await this.fetchItemsConfig();
        await this.fetchMapData();
    }

    fetchItemsConfig = async () => {
        const result = await axios.get(`${Config.getInstance.apiUrl}/api/buildings/config`, {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
        });

        // Otherwise load and show a list with applications
        this.setState({
            buildingsConfig: result.data.data,
        });
    }

    fetchMapData = async () => {
        try {
            const mapUrl = `${Config.getInstance.apiUrl}/api/bundle/singleplayer/${this.props.match.params.bundleId}/${this.state.missionType}/${this.state.mapId}`;
            const response = await axios.get(mapUrl, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });

            if (response.status !== 200 || response.data.status === false) {
                return this.setState({isLoaded: true});
            }

            const mapData = response.data.data;

            this.setState({
                isLoaded: true,
                mapSettings: {
                    name: mapData.name,
                    description: mapData.description,
                    money: mapData.honey,
                    elixir: mapData.elixir,
                    power: mapData.power || 0,
                },
                items: mapData.buildings,
                rewards: mapData.rewards && Array.isArray(mapData.rewards) ? mapData.rewards : [],
            });

        } catch (error) {
            PopupMessage.error("Couldn't load building images");
            this.setState({isLoaded: true});
        }
    }

    prepareJsonForExport = () => {
        this.setState({savingData: true}, () => {
            const jsonToExport = {
                name: this.state.mapSettings.name,
                description: this.state.mapSettings.description,
                honey: this.state.mapSettings.money,
                elixir: this.state.mapSettings.elixir,
                power: this.state.mapSettings.power,
                buildings: this.state.items,
                rewards: this.state.rewards,
            };

            // Save map data
            axios.post(`${Config.getInstance.apiUrl}/singleplayer/save`, {
                data: jsonToExport,
                mapId: this.state.mapId,
                bundleId: this.props.match.params.bundleId,
                missionType: this.state.missionType,
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            })
                .then((result) =>
                    this.setState({
                        savingData: false,
                        redirect: true,
                    }))
                .catch(() => {
                    this.setState({savingData: false}, () => {
                        PopupMessage.error("Couldn't save map data");
                    });
                });
        });
    }

    changeSetting = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const tempSettings = {...this.state.mapSettings};

        switch (key) {
            case "money":
            case "elixir":
            case "power":
                tempSettings[key] = +event.target.value;
                break;
            default:
                tempSettings[key] = event.target.value;
        }

        this.setState({mapSettings: tempSettings});
    }

    addReward = () => {
        this.setState({
            rewards: [...this.state.rewards, {
                itemId: "",
                amount: 0,
            }],
        });
    }

    rewardChange = (e, index) => {
        const tempRewards = [...this.state.rewards];
        const isNumericString = /^\d+$/.test(e.target.value as any);
        const itemId = isNumericString ? +e.target.value : e.target.value as number;

        tempRewards[index].itemId = itemId as any;

        if (this.isAttachment(itemId)) {
            tempRewards[index].level = 1;
        } else {
            delete tempRewards[index].level;
        }

        this.setState({rewards: tempRewards});
    }

    changeAmount = (e, index) => {
        const tempRewards = [...this.state.rewards];
        tempRewards[index].amount = parseInt(e.target.value, 10);
        this.setState({rewards: tempRewards});
    }

    changeLevel = (e, index) => {
        const tempRewards = [...this.state.rewards];
        tempRewards[index].level = parseInt(e.target.value, 10);
        this.setState({rewards: tempRewards});
    }

    removeReward = (index) => {
        const tempRewards = [...this.state.rewards];
        tempRewards.splice(index, 1);
        this.setState({rewards: tempRewards});
    }

    getTypeByItemId = (itemId: number) => {
        const itemObject = getGlobalVar("ITEMS").find(item => item.id === itemId);
        return itemObject ? itemObject.type : null;
    }

    isAttachment = (itemId: number) => {
        const itemType = this.getTypeByItemId(itemId);

        if (!itemType) {
            return false;
        }

        switch (itemType) {
            case "weapon":
            case "torso":
            case "legs":
                return true;
            default:
                return false;
        }
    }

    render() {
        const {classes} = this.props;
        // If we need a redirect to main bundle page
        if (this.state.redirect) {
            return <Redirect to={`/bundle/missions/${this.state.missionType}`}/>;
        }

        const formItems = () => (this.state.rewards || []).map((reward, index) => (
            <div className={classes.formRow} style={{marginTop: 15}} key={`reward_${index}`}>
                <div style={{width: 200, display: "inline-block"}}>
                    <FormControl style={{width: "100%"}}>
                        <InputLabel htmlFor="age-simple">Item</InputLabel>

                        <Select
                            value={reward.itemId}
                            style={{width: "100%"}}
                            onChange={event => this.rewardChange(event, index)}
                            native={true}
                            inputProps={{
                                name: "Item",
                                id: "item-id-select",
                            }}
                        >
                            {getGlobalVar("ITEMS").map(item => (
                                <option value={item.id} key={item.id}>
                                    [{item.id}] {Helpers.getLocalizationName(item.name)}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <div style={{width: 100, display: "inline-block", marginLeft: 10}}>
                    <TextField
                        id="reward-amount"
                        label="Amount"
                        value={reward.amount}
                        onChange={event => this.changeAmount(event, index)}
                        type="number"
                        margin="normal"
                        style={{margin: 0}}
                    />
                </div>

                <div style={{width: 100, display: "inline-block", marginLeft: 10}}>
                    {this.isAttachment(+reward.itemId) &&
                    <TextField
                        id="reward-amount"
                        label="Level"
                        value={reward.level}
                        onChange={event => this.changeLevel(event, index)}
                        type="number"
                        margin="normal"
                        style={{margin: 0}}
                    />
                    }
                </div>

                <div style={{width: 25, display: "inline-block", marginLeft: 10, height: 48}}>
                    <IconButton aria-label="Delete" onClick={() => this.removeReward(index)} size="small"
                                style={{marginTop: 18}}>
                        <DeleteIcon fontSize="inherit"/>
                    </IconButton>
                </div>
            </div>
        ));

        return <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                    <div className={classes.title}>
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small"/>}
                            aria-label="breadcrumb"
                            className="bundle-breadcrumbs"
                        >
                            <Link to={`/bundle/missions/${this.state.missionType}/`} component={RouterLink as any}>
                                All maps
                            </Link>
                            <Typography color="textPrimary">
                                Edit <strong>{this.state.mapSettings.name}</strong>
                            </Typography>
                        </Breadcrumbs>
                    </div>
                </div>

                <div style={{float: "left", width: "100%"}}>
                    <LoadingWrapper loading={!this.state.isLoaded}>
                        <div className={classes.formWrapper}>
                            {this.state.isLoaded && <div>
                                <h3>Settings</h3>

                                <div className={classes.formRow}>
                                    <TextField
                                        label="Name"
                                        value={this.state.mapSettings.name || ""}
                                        margin="normal"
                                        onChange={this.changeSetting("name")}
                                        style={{width: "100%"}}
                                    />
                                </div>

                                <div className={classes.formRow}>
                                    <TextField
                                        label="Description"
                                        value={this.state.mapSettings.description || ""}
                                        margin="normal"
                                        multiline
                                        rows="2"
                                        onChange={this.changeSetting("description")}
                                        style={{width: "100%"}}
                                    />
                                </div>

                                <div className={classes.formRow}>
                                    <TextField
                                        label="Money"
                                        value={this.state.mapSettings.money || 0}
                                        margin="normal"
                                        helperText="Total amount of money that can be stolen from producers/storages"
                                        onChange={this.changeSetting("money")}
                                        type="tel"
                                        inputProps={{
                                            pattern: "[0-9]*",
                                        }}
                                    />
                                </div>

                                <div className={classes.formRow}>
                                    <TextField
                                        label="Elixir"
                                        value={this.state.mapSettings.elixir || 0}
                                        margin="normal"
                                        helperText="Total amount of elixir that can be stolen from producers/storages"
                                        onChange={this.changeSetting("elixir")}
                                        type="tel"
                                        inputProps={{
                                            pattern: "[0-9]*",
                                        }}
                                    />
                                </div>

                                <div className={classes.formRow}>
                                    <TextField
                                        label="Power"
                                        value={this.state.mapSettings.power || 0}
                                        margin="normal"
                                        helperText="Total amount of elixir that can be stolen from producers/storages"
                                        onChange={this.changeSetting("power")}
                                        type="tel"
                                        inputProps={{
                                            pattern: "[0-9]*",
                                        }}
                                    />
                                </div>

                                <h3 style={{marginTop: 15}}>Rewards</h3>

                                <div className={classes.formRow}>
                                    <Button disableElevation onClick={this.addReward} variant="contained">
                                        <AddIcon/> Add reward
                                    </Button>
                                </div>

                                <div className={classes.formRow}>
                                    {formItems()}
                                </div>

                                <div className={classes.formRow} style={{marginTop: 45}}>
                                    <Button disableElevation onClick={this.prepareJsonForExport} disabled={this.state.savingData}
                                            variant="contained" color="primary">
                                        {(this.state.savingData) ? "Saving..." : "Save map"}
                                    </Button>
                                </div>
                            </div>}
                        </div>
                    </LoadingWrapper>
                </div>
            </Paper>
        </div>;
    }
}

const styles: any = theme => ({
    root: {
        width: "100%",
        marginTop: 24,
        overflowX: "auto",
    },
    table: {
        minWidth: 700,
    },
    title: {
        flex: "0 0 auto",
        float: "left",
    },
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },
    tableRow: {
        cursor: "pointer",
    },
    avatar: {
        margin: 3,
        width: 30,
        height: 30,
        fontSize: "0.85rem",
    },
    mapWrapper: {
        display: "block",
        float: "left",
        width: "100%",
        height: 700,
        padding: "0px 22px 0px 22px",
    },
    formWrapper: {
        display: "block",
        float: "left",
        width: "100%",
        padding: "15px 22px 15px 22px",
    },
    formRow: {
        display: "block",
    },
    
});

export const AttackMapEditor = withStyles(styles)(MapEditorWrapped);
